import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Radio, Spin } from 'antd';
import RestHelper from 'utils/RestHelper';
import { ze } from 'utils/zhEn';
import CsSimpleButton from 'components/antd-extension/CsSimpleButton';
import JankFlameGraph from 'components/exception/issue/DataHead/JankFlameGraph';
import {
  ISSUE_JANK_FLAME_SAMPLE_ROWS,
  loadIssueJankFlameTrees,
  summarizeJankFlameGraph,
} from 'utils/jankFlameGraphMerge';

class FlameRenderBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) {
      return <div>{ze('火焰图渲染失败，已跳过异常样本', 'Flame graph failed to render')}</div>;
    }
    return this.props.children;
  }
}

FlameRenderBoundary.propTypes = {
  resetKey: PropTypes.number,
  children: PropTypes.node,
};

function IssueJankFlamePanel({
  singleTree,
  parseFailed,
  appId,
  pid,
  issueId,
  crashId,
  fetchSamples,
  parsedJank,
  rootOnTop,
  minBlockHeight,
  maxHeight,
}) {
  const [mode, setMode] = useState('single');
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [keyword, setKeyword] = useState('');
  const [zoomKey, setZoomKey] = useState(0);
  const loadedKeyRef = useRef('');
  const skipAfterIdentityChangeRef = useRef(false);

  useEffect(() => {
    skipAfterIdentityChangeRef.current = true;
    loadedKeyRef.current = '';
    setMode('single');
    setAggregate(null);
    setError('');
    setKeyword('');
    setZoomKey(0);
    setLoading(false);
  }, [appId, issueId, crashId]);

  useEffect(() => {
    if (skipAfterIdentityChangeRef.current) {
      skipAfterIdentityChangeRef.current = false;
      return undefined;
    }
    if (mode !== 'aggregate') {
      return undefined;
    }
    const cacheKey = `${appId}|${pid}|${issueId}|${fetchSamples ? 1 : 0}|${parsedJank ? 1 : 0}`;
    if (loadedKeyRef.current === cacheKey) {
      return undefined;
    }
    if (!appId || !issueId) {
      setError(ze('缺少项目或问题号，无法聚合', 'Missing app or issue id'));
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    loadIssueJankFlameTrees({
      appId,
      pid,
      issueId,
      fetchSamples,
      parsedJank,
      post: (url, body, options) => RestHelper.mustPost(url, body, options),
    })
      .then((result) => {
        if (!cancelled) {
          loadedKeyRef.current = cacheKey;
          setAggregate(result);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError((e && (e.message || e.description)) || ze('聚合失败', 'Aggregate failed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [mode, appId, pid, issueId, crashId, fetchSamples, parsedJank]);

  const summary = useMemo(() => {
    if (!aggregate || !aggregate.tree) {
      return null;
    }
    return summarizeJankFlameGraph(aggregate.tree);
  }, [aggregate]);

  const chartData = (mode === 'aggregate' && !error)
    ? (aggregate && aggregate.tree)
    : singleTree;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '8px 0' }}>
        <Radio.Group
          size="small"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <Radio.Button value="single">{ze('当前单条上报火焰图', 'Current report flame graph')}</Radio.Button>
          <Radio.Button value="aggregate">
            {ze(`Issue 聚合火焰图（抽样近 ${ISSUE_JANK_FLAME_SAMPLE_ROWS} 条上报）`, `Issue aggregate (latest ${ISSUE_JANK_FLAME_SAMPLE_ROWS})`)}
          </Radio.Button>
        </Radio.Group>
        <Input
          allowClear
          size="small"
          style={{ width: 220 }}
          placeholder={ze('搜索函数名', 'Search function')}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <CsSimpleButton onClick={() => setZoomKey(key => key + 1)}>
          {ze('重置', 'Reset')}
        </CsSimpleButton>
      </div>
      {mode === 'aggregate' && summary && (
        <div style={{ marginBottom: 8, lineHeight: '22px' }}>
          {ze(
            `已聚合 ${summary.totalReports} 条卡顿上报 · 共 ${summary.sampleCount} 个采样点 · 主热点路径：${summary.hotspotPath} (占总采样 ${summary.hotspotPercent.toFixed(1)}% · 覆盖 ${summary.hotspotReportHits}/${summary.totalReports} 条上报)`,
            `Aggregated ${summary.totalReports} reports · ${summary.sampleCount} samples · hotspot: ${summary.hotspotPath} (${summary.hotspotPercent.toFixed(1)}% · ${summary.hotspotReportHits}/${summary.totalReports} reports)`,
          )}
          {aggregate.skipped > 0 && (
            <span>{ze(` · 已跳过 ${aggregate.skipped} 条无效样本`, ` · skipped ${aggregate.skipped}`)}</span>
          )}
        </div>
      )}
      {mode === 'aggregate' && error && <div>{error}</div>}
      <Spin spinning={loading}>
        <div style={{ display: 'flex', justifyContent: 'center', minHeight: 80 }}>
          {parseFailed && mode === 'single' && !chartData && <div>{ze('火焰图数据解析失败', 'Failed to parse flame graph')}</div>}
          {mode === 'aggregate' && !loading && aggregate && !aggregate.tree && (
            <div>{ze('没有可聚合的采样', 'No samples to aggregate')}</div>
          )}
          {chartData && (
            <FlameRenderBoundary resetKey={zoomKey}>
              <JankFlameGraph
                key={zoomKey}
                data={chartData}
                highlightKeyword={keyword}
                rootOnTop={rootOnTop}
                minBlockHeight={minBlockHeight}
                maxHeight={maxHeight}
              />
            </FlameRenderBoundary>
          )}
        </div>
      </Spin>
    </div>
  );
}

IssueJankFlamePanel.propTypes = {
  singleTree: PropTypes.object,
  parseFailed: PropTypes.bool,
  appId: PropTypes.string,
  pid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  issueId: PropTypes.string,
  crashId: PropTypes.string,
  fetchSamples: PropTypes.bool,
  parsedJank: PropTypes.bool,
  rootOnTop: PropTypes.bool,
  minBlockHeight: PropTypes.number,
  maxHeight: PropTypes.number,
};

IssueJankFlamePanel.defaultProps = {
  parseFailed: false,
  fetchSamples: false,
  parsedJank: false,
  rootOnTop: false,
};

export default IssueJankFlamePanel;
